import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Tag, Download, Sparkles, Wrench, Activity } from "lucide-react";

export default function FeaturesTab() {
  return (
    <div className="space-y-6">
      {/* Working with Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Working with Documents
          </CardTitle>
          <CardDescription>Viewing, adding, updating, and organizing documentation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Viewing Documents</h4>
            <p className="text-sm text-muted-foreground">
              All team documentation is available on the Documents page. Use search (Cmd+K) or
              filter by tags to find what you need. Click any document to view its full content,
              metadata, and associated tags.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Adding Documents</h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  <strong>Manual Method:</strong>
                </p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2">
                  <li>Navigate to Documents page</li>
                  <li>Click "Add Document" button</li>
                  <li>Enter URL, name, description, and tags</li>
                  <li>System fetches and processes the documentation</li>
                  <li>Document appears in your team's library</li>
                </ol>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  <strong>AI-Assisted Method (Recommended for bulk):</strong>
                </p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2">
                  <li>Click "AI Discover" button</li>
                  <li>Describe what you need: "Download all MUI documentation"</li>
                  <li>AI suggests 10-20 official docs (filters out existing)</li>
                  <li>Click "Add All Documents" for bulk download</li>
                  <li>All documents are processed automatically</li>
                </ol>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Updating Documents</h4>
            <p className="text-sm text-muted-foreground">
              Documents can be updated by clicking the edit icon on any document card. Update the
              name, description, or tags to improve discoverability. Content is automatically
              refreshed during automated maintenance runs.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Tagging Documents</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Tags make documentation discoverable. Use 5-7 tags per document including both
              technology (react, python, docker) and purpose (api-reference, tutorial, deployment)
              tags. Always use lowercase for consistency.
            </p>
            <div className="mt-2 p-3 bg-muted rounded-md">
              <p className="text-sm">
                <strong>Best Practice:</strong> Tag thoroughly when adding documents - it's easier
                to filter later than to search through thousands of docs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Working with Tags */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Working with Tags
          </CardTitle>
          <CardDescription>Filtering and managing documentation tags</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Tag Filtering</h4>
            <p className="text-sm text-muted-foreground">
              Click any tag badge on a document to filter all documents with that tag. Multiple tags
              can be selected to narrow results. Use the tag selector in the Documents page toolbar
              to filter by multiple tags at once.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Tag Types</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h5 className="font-semibold mb-2 text-sm">Technology Tags</h5>
                <div className="flex flex-wrap gap-1">
                  <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/20 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-400/20">
                    react
                  </span>
                  <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/20 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-400/20">
                    python
                  </span>
                  <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/20 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-400/20">
                    docker
                  </span>
                  <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/20 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-400/20">
                    typescript
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Tag the specific technology</p>
              </div>
              <div>
                <h5 className="font-semibold mb-2 text-sm">Purpose Tags</h5>
                <div className="flex flex-wrap gap-1">
                  <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-700/10 dark:ring-green-400/20">
                    api-reference
                  </span>
                  <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-700/10 dark:ring-green-400/20">
                    tutorial
                  </span>
                  <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-700/10 dark:ring-green-400/20">
                    deployment
                  </span>
                  <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400 ring-1 ring-inset ring-green-700/10 dark:ring-green-400/20">
                    best-practices
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Tag what type of documentation</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Tag Management</h4>
            <p className="text-sm text-muted-foreground">
              Tags are automatically extracted from all documents and displayed in the tag selector.
              Popular tags appear with usage counts. You can manage tags by editing document
              metadata or using the bulk tag editor.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Exports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Exports
          </CardTitle>
          <CardDescription>Creating and using documentation exports</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Creating Exports</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li>Navigate to Exports page</li>
              <li>Click "Create Export" button</li>
              <li>Choose format and select tags to filter</li>
              <li>Wait for processing to complete</li>
              <li>Download export file for your project</li>
            </ol>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Export Formats</h4>
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">Claude Format</p>
                <p className="text-xs text-muted-foreground">
                  Optimized for Claude Code integration. Includes full markdown content with
                  metadata headers. Best for AI coding assistants.
                </p>
              </div>

              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">TOC Format</p>
                <p className="text-xs text-muted-foreground">
                  Table of Contents with links to original sources. Lightweight format for quick
                  reference and sharing.
                </p>
              </div>

              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">JSON Format</p>
                <p className="text-xs text-muted-foreground">
                  Structured data export with full metadata. Use for custom integrations or
                  programmatic access.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Using Exports</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">For AI Integration:</strong> Use Claude format
                exports and add to your project's{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">docs/localdocs/</code>{" "}
                folder. Claude Code will automatically discover and use the documentation.
              </p>
              <p>
                <strong className="text-foreground">For Project Setup:</strong> Export filtered by
                technology tags (e.g., "react", "postgres", "docker") and share with team members
                for quick onboarding.
              </p>
              <p>
                <strong className="text-foreground">For Client Handoffs:</strong> Create TOC or JSON
                exports filtered by client-specific tags to provide clean documentation packages.
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm">
              <strong>💡 Pro Tip:</strong> Filter exports by multiple tags to create focused
              documentation sets for specific projects. Exports are cached for 7 days for faster
              re-downloads.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* AI Discovery */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI Document Discovery
          </CardTitle>
          <CardDescription>
            Powered by Claude Sonnet 4.5 for intelligent documentation discovery
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">What is AI Document Discovery?</h4>
            <p className="text-sm text-muted-foreground">
              AI-powered assistant that helps you discover and download comprehensive documentation
              sets with a single request. It automatically filters out documentation you already
              have and suggests official sources only.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">How to Use</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>
                Navigate to Documents page and click{" "}
                <strong className="text-foreground">"AI Discover"</strong> button
              </li>
              <li>
                Describe what you need in natural language:
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                  <li>"Download all MUI documentation"</li>
                  <li>"Find comprehensive React docs"</li>
                  <li>"I need FastAPI deployment guides"</li>
                </ul>
              </li>
              <li>
                AI suggests 10-20 official documentation URLs (getting started, API reference,
                components, examples, deployment guides)
              </li>
              <li>
                Review suggestions and click{" "}
                <strong className="text-foreground">"Add All Documents"</strong> for bulk download
              </li>
              <li>All documents are queued for processing automatically</li>
            </ol>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Key Features</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">Duplicate Filtering:</strong> Automatically
                excludes documentation you already have
              </li>
              <li>
                <strong className="text-foreground">Bulk Download:</strong> Add 10-20 docs with one
                click instead of manual entry
              </li>
              <li>
                <strong className="text-foreground">Official Sources Only:</strong> AI prioritizes
                official documentation over third-party guides
              </li>
              <li>
                <strong className="text-foreground">Comprehensive Coverage:</strong> Suggests
                complete documentation sets (tutorials, API, examples, deployment)
              </li>
              <li>
                <strong className="text-foreground">Smart Suggestions:</strong> Claude Sonnet 4.5
                understands context and provides relevant results
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">AI Discovery Workflow</h4>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <p className="font-semibold text-foreground">Request</p>
                  <p>Describe what documentation you need in natural language</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <p className="font-semibold text-foreground">Discovery</p>
                  <p>AI searches for official documentation and filters out duplicates</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <p className="font-semibold text-foreground">Review</p>
                  <p>Review suggested URLs and metadata before bulk import</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                  4
                </div>
                <div>
                  <p className="font-semibold text-foreground">Import</p>
                  <p>Bulk import all suggestions with one click</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-primary/10 rounded-md border border-primary/20">
            <p className="text-sm">
              <strong>💡 Pro Tip:</strong> Instead of manually searching and adding individual docs,
              ask the AI to "download all X documentation" for entire frameworks. This saves hours
              of manual work and ensures you get comprehensive coverage.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Maintenance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Maintenance & Quality Control
          </CardTitle>
          <CardDescription>Automated maintenance and quality monitoring</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Quality Check</h4>
            <p className="text-sm text-muted-foreground">
              Run automated quality checks on your documentation library to identify documents with
              missing metadata, insufficient tags, or processing issues. Issues are categorized by
              priority:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground ml-2 mt-2 space-y-1">
              <li>
                <strong className="text-red-600 dark:text-red-400">High:</strong> Missing content,
                processing failed, or document too large
              </li>
              <li>
                <strong className="text-amber-600 dark:text-amber-400">Medium:</strong> Missing
                title/description, insufficient tags (&lt;3 tags)
              </li>
              <li>
                <strong className="text-blue-600 dark:text-blue-400">Low:</strong> Short description
                (&lt;20 characters)
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Auto-Fix Issues</h4>
            <p className="text-sm text-muted-foreground">
              Automatically fix common quality issues with one click:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground ml-2 mt-2 space-y-1">
              <li>
                <strong>Missing titles:</strong> Generated from URL or path
              </li>
              <li>
                <strong>Missing descriptions:</strong> Extracted from document content
              </li>
              <li>
                <strong>Short descriptions:</strong> Enhanced with additional content
              </li>
              <li>
                <strong>Insufficient tags:</strong> Auto-generated from URL, title, and content
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Real-Time Monitoring (Telescope-Style)
            </h4>
            <p className="text-sm text-muted-foreground">
              The <strong>Monitor</strong> tab provides real-time visibility into background
              maintenance tasks, similar to Laravel Telescope:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground ml-2 mt-2 space-y-1">
              <li>View active tasks with progress bars (0-100%)</li>
              <li>Track task status: queued → running → completed/failed</li>
              <li>See which documents are being processed</li>
              <li>Auto-refreshes every 2 seconds</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Maintenance History</h4>
            <p className="text-sm text-muted-foreground">
              The <strong>History</strong> tab shows all past maintenance runs. Click any completed
              run to view:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground ml-2 mt-2 space-y-1">
              <li>Full quality report with issue breakdowns</li>
              <li>Documents affected and fixes applied</li>
              <li>Task duration and completion time</li>
              <li>Error details for failed tasks</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Automated Maintenance Jobs
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              LocalDocs Hosted runs automated maintenance jobs to keep your documentation fresh and
              organized. All jobs are visible in the <strong>Jobs</strong> page with real-time
              progress tracking.
            </p>

            <div className="space-y-3 text-sm">
              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">Daily Document Updates</p>
                <p className="text-xs text-muted-foreground mb-1">Every day at 1:00 AM UTC</p>
                <p className="text-xs">
                  Re-downloads and refreshes content for documents older than 7 days. Ensures
                  documentation stays current with the latest updates from source URLs.
                </p>
              </div>

              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">Weekly Quality Check</p>
                <p className="text-xs text-muted-foreground mb-1">Every Wednesday at 3:00 AM UTC</p>
                <p className="text-xs">
                  Scans all documents for quality issues (missing metadata, insufficient tags,
                  processing errors) and automatically fixes simple problems like missing titles or
                  descriptions.
                </p>
              </div>

              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">Weekly Comprehensive Maintenance</p>
                <p className="text-xs text-muted-foreground mb-1">Every Sunday at 2:00 AM UTC</p>
                <p className="text-xs">
                  Full maintenance cycle including quality checks, document updates, and cleanup.
                  Updates ALL documents (up to 500), fixes quality issues, and removes old data
                  older than 30 days.
                </p>
              </div>

              <div className="p-3 bg-muted/50 rounded-md border">
                <p className="font-semibold mb-1">Monthly Deep Cleanup</p>
                <p className="text-xs text-muted-foreground mb-1">
                  First Sunday of month at 4:00 AM UTC
                </p>
                <p className="text-xs">
                  Deep cleanup removing old jobs, exports, and export files older than 60 days.
                  Retries failed documents to give them another chance at processing.
                </p>
              </div>
            </div>

            <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
              <p className="text-xs">
                <strong>✨ Monitor Progress:</strong> Visit the <strong>Jobs</strong> page to see
                all maintenance jobs running in real-time. Each job shows progress (0-100%), status,
                and detailed results when complete.
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm">
              <strong>💡 Pro Tip:</strong> Run quality checks regularly (weekly/monthly) to maintain
              high-quality documentation. Use auto-fix to quickly resolve common issues, then
              manually review high-priority items.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
